import { css } from '@emotion/react';
import { TextareaAutosize } from '@mui/base';
import type { TextareaOwnerState } from '@mui/joy';
import { styled } from '@mui/joy';
import type { JSX } from 'react';
import { Fragment, useId } from 'react';

const StyledTextarea = styled(TextareaAutosize)(css`
  --Textarea-paddingBlock: 0;
  resize: none;
  border: none;
  min-width: 0;
  outline: 0;
  padding: 0;
  padding-block: 1.25em 0.75em;
  padding-inline-end: var(--Textarea-paddingInline);
  flex: auto;
  align-self: stretch;
  color: inherit;
  background-color: transparent;
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  line-height: inherit;

  &::placeholder {
    opacity: 0;
    transition: 0.1s ease-out;
  }

  &:focus::placeholder {
    opacity: 1;
  }

  &:focus + textarea + label,
  &:not(:placeholder-shown) + textarea + label {
    top: 0.4rem;
    font-size: 0.75rem;
  }
`);

const StyledLabel = styled('label')(
  ({ theme }) => css`
    position: absolute;
    top: 1em;
    line-height: 1;
    color: ${theme.vars.palette.text.tertiary};
    font-weight: ${theme.vars.fontWeight.md};
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);

    .Mui-error & {
      color: ${theme.vars.palette.danger['500']};
    }
  `,
);

export function FloatingTextarea({
  label,
  ownerState,
  ...props
}: JSX.IntrinsicElements['textarea'] & { label: string; ownerState: TextareaOwnerState }) {
  const id = useId();

  return (
    <Fragment>
      <StyledTextarea {...props} id={id} placeholder={props.placeholder ?? ''} />
      <StyledLabel htmlFor={id}>{label}</StyledLabel>
    </Fragment>
  );
}
